/* MagicLinkModal — paste a public file URL → backend fetches + AI-parses.
 *
 * Auto-detects + converts:
 *   - Google Drive share link → uc?export=download
 *   - Google Sheets share link → export?format=csv&gid=…
 *   - Dropbox share link → ?dl=1
 *   - Direct HTTPS file URLs (no conversion needed)
 */
import { useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Link as LinkIcon, Sparkles, ArrowRight, Cloud, ShoppingBag, FileText, FileSpreadsheet } from "lucide-react";

const RECOGNIZED = [
  { test: /docs\.google\.com\/spreadsheets/, l: "Google Sheet",   icon: Cloud,        hint: "Auto-converts to CSV export. Sheet must be 'anyone with link can view'." },
  { test: /drive\.google\.com/,               l: "Google Drive",   icon: Cloud,        hint: "Auto-converts to direct download. File must be 'anyone with link can view'." },
  { test: /dropbox\.com/,                     l: "Dropbox link",   icon: Cloud,        hint: "We'll switch ?dl=0 → ?dl=1 for direct download." },
  { test: /\.csv(\?|$)/i,                     l: "Direct CSV URL", icon: FileSpreadsheet, hint: "" },
  { test: /\.xlsx?(\?|$)/i,                   l: "Direct Excel URL", icon: FileSpreadsheet, hint: "" },
  { test: /\.pdf(\?|$)/i,                     l: "Direct PDF URL", icon: FileText,     hint: "" },
];

export default function MagicLinkModal({ profiles, onClose, onPreview }) {
  const [url, setUrl] = useState("");
  const [profileId, setProfileId] = useState("");
  const [busy, setBusy] = useState(false);

  const detected = useMemo(() => RECOGNIZED.find(r => r.test.test(url || "")), [url]);

  const submit = async () => {
    if (!url || !url.startsWith("http")) {
      toast.error("Paste a URL starting with http:// or https://");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/seller/catalog-import/extract-url", {
        url, profile_id: profileId || null,
      });
      // Hand off to the preview modal (same component used by file upload)
      onPreview(r.data);
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not fetch and parse that URL";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="magic-link-modal">
        <DialogHeader>
          <DialogTitle className="display text-2xl tracking-tight inline-flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-foreground/55"/>Magic Link import
          </DialogTitle>
          <DialogDescription>
            Paste any public file URL — verqo will fetch it and run the same AI mapping you&apos;d get from an upload.
            Works with Google Drive / Sheets share links, Dropbox links, and any direct CSV / Excel / PDF URL.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="ml-url">File URL</Label>
            <Input
              id="ml-url"
              autoFocus
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/1Abc.../edit?gid=0"
              className="mt-1.5 h-11 mono text-[12.5px]"
              data-testid="magic-url-input"
            />
            {url && detected && (
              <div className="mt-2 inline-flex items-center gap-2 text-[11.5px] text-foreground/65">
                <detected.icon className="w-3.5 h-3.5"/>
                Detected: <strong className="text-foreground">{detected.l}</strong>
                {detected.hint && <span className="text-foreground/55">· {detected.hint}</span>}
              </div>
            )}
            {url && !detected && url.startsWith("http") && (
              <div className="mt-2 text-[11.5px] text-foreground/55">
                Will fetch the URL as-is and try to detect the file type from its <span className="mono">Content-Type</span>.
              </div>
            )}
          </div>

          {profiles && profiles.length > 0 && (
            <div>
              <Label htmlFor="ml-profile">Apply saved profile (optional)</Label>
              <select id="ml-profile" value={profileId} onChange={(e) => setProfileId(e.target.value)}
                className="mt-1.5 w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
                <option value="">— infer columns each time (slower) —</option>
                {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <p className="text-[11px] text-foreground/55 mt-1">
                Picking a saved profile makes the daily refresh cost $0 in AI.
              </p>
            </div>
          )}

          {/* Examples */}
          <div className="rounded-xl bg-secondary/60 border border-border p-3 text-[11px] text-foreground/65 space-y-1">
            <div className="mono uppercase tracking-wider text-[9.5px] text-foreground/50 mb-1">Examples</div>
            <div><span className="text-foreground/80">Google Sheet:</span> <span className="mono break-all">https://docs.google.com/spreadsheets/d/&lt;id&gt;/edit#gid=0</span></div>
            <div><span className="text-foreground/80">Dropbox file:</span> <span className="mono break-all">https://www.dropbox.com/s/&lt;…&gt;/inventory.csv?dl=0</span></div>
            <div><span className="text-foreground/80">Any HTTPS file:</span> <span className="mono break-all">https://example.com/static/inventory.xlsx</span></div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="magic-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !url}
            data-testid="magic-submit"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90">
            {busy ? "Fetching…" : <>Fetch &amp; preview <ArrowRight className="w-3.5 h-3.5 ml-1.5"/></>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
